import { useEffect, useMemo, useState } from "react";
import { Loader2, MapPin, Gavel, Timer, Users } from "lucide-react";
import { useI18n } from "@/lib/I18nContext";
import {
  useAuctionsNearby,
  formatRemaining,
  secondsUntil,
} from "@/lib/auctions";
import AuctionDetailModal from "@/components/AuctionDetailModal";

// Phase 22b — MatchBid live-feed of nearby auctions.
// - 3-second polling driven by `useAuctionsNearby`.
// - Card countdown ticks every 1 s off `ends_at` (no extra polling).
// - Click a card → opens `AuctionDetailModal` with 1.5-s poll for
//   the live bid history + current price.

export default function MatchBidList({ onCreateAuction }) {
  const { t } = useI18n();
  const { data, isLoading } = useAuctionsNearby(true);
  const [detailId, setDetailId] = useState(null);
  const auctions = data?.auctions || [];

  return (
    <div data-testid="matchbid-list" className="space-y-4">
      <div className="flex items-center justify-between">
        <p className="text-xs text-[#A1A1AA] font-body">
          {t(
            "matchbid.list_header",
            "Live auctions within 5km — updated every 3 seconds."
          )}
        </p>
        <button
          type="button"
          onClick={onCreateAuction}
          data-testid="matchbid-create-auction"
          className="h-9 px-4 rounded-md bg-transparent border border-[#E5B83A]/60 text-[#E5B83A] font-medium text-xs hover:bg-[#E5B83A]/10 transition-colors flex items-center gap-2"
        >
          <Gavel className="h-3.5 w-3.5" />
          {t("matchbid.create_button", "Start an auction")}
        </button>
      </div>

      {isLoading && auctions.length === 0 ? (
        <div
          data-testid="matchbid-loading"
          className="flex items-center justify-center py-16 text-white/60"
        >
          <Loader2 className="h-5 w-5 animate-spin" />
        </div>
      ) : auctions.length === 0 ? (
        <div
          data-testid="matchbid-empty"
          className="rounded-lg border border-white/10 bg-black/40 px-6 py-10 text-center text-sm text-[#A1A1AA] font-body"
        >
          {t(
            "matchbid.empty",
            "No live auctions within 5km. Start one yourself."
          )}
        </div>
      ) : (
        <div
          data-testid="matchbid-grid"
          className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4"
        >
          {auctions.map((a) => (
            <AuctionCard
              key={a.id}
              auction={a}
              onClick={() => setDetailId(a.id)}
            />
          ))}
        </div>
      )}

      <AuctionDetailModal id={detailId} onClose={() => setDetailId(null)} />
    </div>
  );
}

// ── Card ──────────────────────────────────────────────────────────────
function AuctionCard({ auction, onClick }) {
  const { t } = useI18n();
  const primaryPhoto =
    auction.photo_urls && auction.photo_urls.length > 0
      ? auction.photo_urls[0]
      : null;
  const distance = Number.isFinite(auction.distance_meters)
    ? `${Math.round(auction.distance_meters)}m`
    : null;

  // Live tick — every second, without extra network calls.
  const [tick, setTick] = useState(() => secondsUntil(auction.ends_at));
  useEffect(() => {
    setTick(secondsUntil(auction.ends_at));
    const id = setInterval(
      () => setTick(secondsUntil(auction.ends_at)),
      1000
    );
    return () => clearInterval(id);
  }, [auction.ends_at]);

  const critical = tick <= 30;
  const urgent = tick <= 10;
  const countdownClass = useMemo(() => {
    const base =
      "font-mono text-xs tabular-nums transition-colors flex items-center gap-1";
    if (tick <= 0) return `${base} text-[#FF6B6B]`;
    if (urgent) return `${base} text-[#FF3B3B] countdown-critical-pulse`;
    if (critical) return `${base} text-[#FF6B6B]`;
    return `${base} text-[#E5B83A]`;
  }, [tick, urgent, critical]);

  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={`auction-card-${auction.id}`}
      data-time-remaining-sec={tick}
      className="text-left rounded-lg border border-[#FFD700]/25 bg-[#0A0A0A] overflow-hidden hover:border-[#FFD700]/60 transition-colors group"
    >
      <div className="aspect-[4/3] bg-black flex items-center justify-center overflow-hidden">
        {primaryPhoto ? (
          <img
            src={primaryPhoto}
            alt={`Sticker #${auction.number}`}
            className="w-full h-full object-cover group-hover:scale-[1.02] transition-transform"
          />
        ) : (
          <div className="flex flex-col items-center gap-2 text-white/40 font-mono">
            <Gavel className="h-6 w-6" />
            <span className="text-xs">#{auction.number}</span>
          </div>
        )}
      </div>
      <div className="p-3 space-y-1.5">
        <div className="flex items-center justify-between">
          <span className="font-mono text-sm text-white">
            #{auction.number}
          </span>
          <span
            className="text-[10px] uppercase tracking-widest text-[#FFD700] font-body"
            data-testid={`auction-grade-${auction.id}`}
          >
            {auction.grade}
          </span>
        </div>
        <p
          className="font-display text-lg font-semibold text-[#E5B83A]"
          data-testid={`auction-current-price-${auction.id}`}
        >
          {auction.current_price} FT
          <span className="ml-2 text-[10px] uppercase tracking-widest text-[#A1A1AA] font-body">
            {auction.bid_count > 0
              ? t("matchbid.current_bid", "current bid")
              : t("matchbid.starting_at", "starting at")}
          </span>
        </p>
        <div className="flex items-center justify-between text-[11px] text-[#A1A1AA] font-body">
          <span className="flex items-center gap-1">
            <Users className="h-3 w-3" />
            <span data-testid={`auction-bid-count-${auction.id}`}>
              {auction.bid_count}
            </span>{" "}
            {t("matchbid.bids_short", "bids")}
          </span>
          <span
            className={countdownClass}
            data-testid={`auction-countdown-${auction.id}`}
          >
            <Timer className="h-3 w-3" />
            {formatRemaining(tick)}
          </span>
        </div>
        <div className="flex items-center justify-between text-[11px] text-[#A1A1AA] font-body pt-1">
          <span>{auction.seller_display_name || "—"}</span>
          {distance ? (
            <span className="flex items-center gap-1">
              <MapPin className="h-3 w-3" />
              {distance}
            </span>
          ) : null}
        </div>
      </div>
    </button>
  );
}
